import { Box, Checkbox, IconButton, lighten, makeStyles, Tooltip, Typography, useTheme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, {
  components,
  MenuProps,
  MultiValueProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';

import MenuHeader from './MenuHeader';

export interface SelectOption {
  label: string;
  value: string;
}
export type SelectOptionChangeParam = SelectOption[] | SelectOption | null;

interface Props {
  name?: string;
  label: string;
  options?: SelectOption[];
  required?: boolean;
  disabled?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  onChange?: (value: SelectOptionChangeParam) => void;
  value?: any;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  valueNotChip?: boolean;
  onBlur?: () => void;
  menuPlacement?: 'top' | 'bottom';
  showErrorBeforeBlur?: boolean;
  disableSorting?: boolean;
  maxVisibleChips?: number;
}

function CheckboxOption({ children, ...props }: OptionProps<SelectOption>) {
  const classes = useOptionStyles();

  return (
    <components.Option
      {...props}
      className={classes.optionHover}
      innerProps={
        {
          ...props.innerProps,
          role: 'menuitem',
          'data-testid': props.data?.label || props.data?.value,
        } as typeof props.innerProps
      }
    >
      <div className={classes.optionContainer}>
        {props.selectProps.isMulti && (
          <Checkbox checked={props.isSelected} className={classes.checkbox} classes={{ root: classes.checkboxRoot }} />
        )}
        <span className={props.selectProps.isMulti ? classes.labelWithCheckbox : classes.labelWithoutCheckbox}>
          {children}
        </span>
      </div>
    </components.Option>
  );
}

function ValueContainer(valueContainerProps: ValueContainerProps<any>) {
  const { selectProps, children } = valueContainerProps;

  return (
    <components.ValueContainer {...valueContainerProps}>
      <div className={selectProps.classes.valueLabelContainer}>
        <div className={selectProps.classes.valueLabel} data-testid={selectProps.name}>
          {selectProps.label} {selectProps.required ? <div style={{ color: 'red' }}>*</div> : ''}
        </div>
        <div className={selectProps.classes.selectedOptionsContainer}>{children}</div>
      </div>
    </components.ValueContainer>
  );
}

function SingleValue(props: SingleValueProps<SelectOption>) {
  const { children, selectProps } = props;

  return (
    <div
      className={selectProps.classes.selectedOptionsLabels}
      style={selectProps.isDisabled ? { color: 'rgba(0, 0, 0, 0.38)' } : undefined}
    >
      {children}
    </div>
  );
}

export function Menu(menuProps: MenuProps<any>) {
  const { selectProps, children } = menuProps;
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!event.target) return;
      const target = event.target as Node;
      const deleteChips = Array.from(document.querySelectorAll('[id^="deleteChip-"]'));
      const isTargetOnDeleteChip = deleteChips.some((element) => element?.contains(target));

      setTimeout(() => {
        if (menuRef.current && !menuRef.current?.contains?.(target) && !isTargetOnDeleteChip) {
          selectProps.onMenuClose?.();
        }
      }, 50);
    },
    [selectProps],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, { capture: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, { capture: true });
    };
  }, [handleClickOutside]);

  const props = selectProps as any;

  return (
    <components.Menu {...menuProps}>
      <div ref={menuRef} id="menu">
        {props.isMulti && !selectProps.hideSelectAllOption ? (
          <MenuHeader
            handleClearAll={() => props.onChange?.([])}
            handleSelectAll={() => {
              if (selectProps.options) {
                props.onChange?.(selectProps.options);
              }
            }}
          />
        ) : null}
        <div>{children}</div>
      </div>
    </components.Menu>
  );
}

function MultiValue(props: MultiValueProps<SelectOption>) {
  const { data, getValue, children, selectProps } = props;
  const allSelected = getValue() as SelectOption[];
  const index = allSelected.findIndex((o) => o.value === data.value);
  const maxVisible = (selectProps as any).maxVisibleChips ?? 10;

  if (index >= maxVisible) return null;

  if (index === maxVisible - 1 && allSelected.length > maxVisible) {
    const remaining = allSelected.slice(maxVisible);
    const tooltipText = remaining.map((o) => o.label).join(', ');

    return (
      <Tooltip title={tooltipText} placement="top">
        <div
          className={selectProps.classes.selectedOptionsLabels}
          style={selectProps.isDisabled ? { color: 'rgba(0, 0, 0, 0.38)' } : undefined}
        >
          <span style={{ paddingTop: '2px' }}>+ {allSelected.length - maxVisible + 1} more</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <div
      className={selectProps.classes.selectedOptionsLabels}
      style={selectProps.isDisabled ? { color: 'rgba(0, 0, 0, 0.38)' } : undefined}
    >
      <span style={{ paddingTop: '2px' }}>{children}</span>
      <IconButton
        key="close"
        aria-label="Close"
        color="inherit"
        className={selectProps.classes.closeIconButton}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          const newValue = allSelected.filter((option) => option.value !== data.value);
          selectProps.onChange?.(newValue, { action: 'remove-value' } as any);
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        size="small"
      >
        <CloseIcon className={selectProps.classes.closeIcon} />
      </IconButton>
    </div>
  );
}

function Placeholder(props: PlaceholderProps<any>) {
  const { hasValue, selectProps } = props;
  if (hasValue || selectProps.menuIsOpen) return null;

  return <span className={selectProps.classes.placeholderLabel}>{selectProps.placeholder ?? 'Select...'}</span>;
}

function MenuList(menuListProps: React.ComponentProps<typeof components.MenuList>) {
  return (
    <components.MenuList {...menuListProps}>
      <div id="menu-list">{menuListProps.children}</div>
    </components.MenuList>
  );
}

export default function SelectDropdown({
  value,
  onChange,
  onBlur,
  required,
  showErrorBeforeBlur,
  options,
  isMulti = false,
  disabled = false,
  error,
  touched,
  valueNotChip,
  isLoading,
  isClearable = true,
  disableSorting = false,
  maxVisibleChips,
  ...props
}: Props) {
  const classes = useStyles();
  const [hasInteracted, setHasInteracted] = useState(false);
  const theme = useTheme();

  const errorMessage = error || '';
  const showError = !!errorMessage && (hasInteracted || !!showErrorBeforeBlur || !!touched);
  const hoverColor = lighten(theme.palette.primary.main, 0.5) + ' !important';

  const sortedOptions = useMemo(() => {
    if (disableSorting) return options;

    return options?.sort((a, b) => a.label.localeCompare(b.label));
  }, [options, disableSorting]);

  return (
    <Box style={{ width: '100%' }} mb={showError ? 0 : '22px'}>
      <Select
        error={error}
        defaultOptions
        disableUnderline
        styles={customStyles(theme, showError)}
        isSearchable
        isClearable={isClearable}
        backspaceRemovesValue={false}
        isLoading={isLoading}
        isMulti={isMulti}
        onMenuOpen={() => setHasInteracted(true)}
        onMenuClose={() => setHasInteracted(true)}
        menuPlacement="auto"
        closeMenuOnSelect={!isMulti}
        components={{
          ValueContainer: ValueContainer,
          Option: CheckboxOption as any,
          Menu: Menu,
          MenuList: MenuList,
          SingleValue: SingleValue,
          MultiValue: MultiValue,
          Placeholder: Placeholder,
        }}
        classes={{
          root: classes.root,
          valueContainer: classes.valueContainer,
          valueLabelContainer: classes.valueLabelContainer,
          valueLabel: classes.valueLabel,
          placeholderLabel: classes.placeholderLabel,
          selectedOptionsContainer: classes.selectedOptionsContainer,
          selectedOptionsLabels: !valueNotChip ? classes.selectedOptionsLabels : classes.plainValue,
          closeIconButton: classes.closeIconButton,
          closeIcon: classes.closeIcon,
        }}
        maxVisibleChips={maxVisibleChips}
        {...props}
        onBlur={() => {
          setHasInteracted(true);
          onBlur?.();
        }}
        options={sortedOptions}
        onChange={(value: any) => onChange?.(value)}
        isDisabled={disabled}
        value={value}
        hideSelectedOptions={false}
        menuPortalTarget={document.body}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={(e: Event) => {
          const target = e.target as HTMLElement;
          if (target === document.documentElement || target === document.body || target?.id === 'modalBody') {
            return true;
          }
          return false;
        }}
        required={required}
        theme={(selectTheme) => ({
          ...selectTheme,
          colors: {
            ...selectTheme.colors,
            primary25: hoverColor,
            primary: isMulti ? 'transparent' : `${lighten(theme.palette.primary.main, 0.25)}`,
          },
        })}
      />
      {showError ? (
        <Box mr="14px" ml="14px">
          <Typography
            style={{
              margin: 0,
              fontSize: '0.75em',
              marginTop: 3,
              lineHeight: 1.66,
              color: disabled ? theme.palette.text.disabled : theme.palette.error.main,
            }}
          >
            {errorMessage}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    marginTop: 0,
    marginBottom: 0,
    height: 72,
    backgroundColor: 'transparent',
  },
  valueContainer: {
    width: '100%',
    display: 'flex',
    height: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    font: 'inherit',
    color: 'inherit',
  },
  valueLabel: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: '4px',
  },
  valueLabelContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '14px 8px 8px 8px',
    gridGap: 4,
  },
  placeholderLabel: {
    fontSize: 16,
    color: '#E0E0E0',
  },
  selectedOptionsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
  },
  selectedOptionsLabels: {
    backgroundColor: `${
      theme.palette?.primary?.main?.replace('rgb', 'rgba')?.replace(')', ', 0.1)') || 'rgba(68, 113, 184, 0.1)'
    }`,
    color: theme.palette?.primary?.main || '#4471B8',
    border: `2px solid ${theme.palette?.primary?.main || '#4471B8'}`,
    borderRadius: '50px',
    padding: '3px 6px',
    fontSize: '14px',
    fontWeight: 800,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  plainValue: {
    fontSize: '16px',
  },
  closeIconButton: {
    backgroundColor: theme.palette?.primary?.main || '#4471B8',
    width: '20px',
    height: '20px',
    color: '#FFFFFF',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette?.primary?.main || '#4471B8',
      opacity: 0.8,
    },
  },
  closeIcon: {
    fontSize: '16px',
    color: '#FFFFFF',
  },
}));

const customStyles = (theme: any, error?: boolean) => ({
  control: (provided: any, state: { isFocused: boolean; isDisabled: boolean }) => ({
    ...provided,
    width: '100%',
    minHeight: '80px',
    borderWidth: '1px',
    borderColor: error && !state.isDisabled ? 'red' : state.isFocused ? theme.palette.primary.main : '#E0E0E0',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    borderRadius: 4,
    color: state.isDisabled ? 'rgba(0, 0, 0, 0.38)' : provided.color,
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    height: '100%',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    marginLeft: 2,
    marginRight: 2,
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    alignItems: 'center',
  }),
  input: (provided: any) => ({
    ...provided,
    '& input': {
      font: 'inherit',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: theme.palette.primary.main,
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000000,
    width: '100%',
    padding: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    overflowX: 'hidden',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000000,
  }),
  option: (provided: any) => ({
    ...provided,
    color: 'inherit',
    padding: '6px 0px 6px 0px',
  }),
});

const useOptionStyles = makeStyles((theme: any) => ({
  optionContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '4px 8px',
  },
  checkbox: {
    padding: '4px',
    marginRight: '4px',
  },
  checkboxRoot: {
    color: theme.palette?.primary?.main || '#4471B8',
    '&.Mui-checked': {
      color: theme.palette?.primary?.main || '#4471B8',
    },
  },
  labelWithCheckbox: {
    marginTop: '2px',
  },
  labelWithoutCheckbox: {
    padding: '6px',
  },
  optionHover: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));
